/**
 * A small "?" a child can tap to find out what a number means.
 *
 * **Tap, not hover.** A tooltip that appears on hover does not exist on a
 * tablet, which is what these children use. So this is a real button with a
 * real panel underneath it.
 *
 * **It stays open until it is closed.** A panel that vanishes when a finger
 * moves is a panel a seven-year-old cannot read. There is no timeout.
 *
 * **Written for the child, not the parent.** "Words grown" needs explaining in
 * terms of what the child DID — got it right on twelve different days — not in
 * terms of vectors and floors. If the explanation needs the word "mastery" it
 * is the wrong explanation.
 */
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { radius, theme, TAP_MIN } from '../theme'

export function Explain({
  label, children, align,
}: {
  /** What is being explained, for the screen reader: "what words grown means". */
  label: string
  children: ReactNode
  /** Force a side. Left alone, the panel works out which side fits. */
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const [side, setSide] = useState<'left' | 'right'>(align ?? 'left')
  const button = useRef<HTMLButtonElement>(null)

  /**
   * Open on whichever side has room.
   *
   * The three numbers on the summary sit in a row, and the "?" on the third
   * one is close to the right edge. A panel always anchored on its left ran
   * off the screen there — on a phone that is most of the explanation gone,
   * and it is the "words grown" one, the number this app most wants read.
   */
  const show = () => {
    const box = button.current?.getBoundingClientRect()
    if (!align && box) {
      setSide(box.left + 250 > window.innerWidth - 12 ? 'right' : 'left')
    }
    setOpen(true)
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={button}
        type="button"
        aria-label={open ? `Hide ${label}` : `What does this mean? ${label}`}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); if (open) setOpen(false); else show() }}
        style={{
          fontFamily: 'inherit',
          width: 22, height: 22, padding: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 13, fontWeight: 800, lineHeight: 1,
          color: open ? '#FFFFFF' : theme.muted,
          background: open ? theme.orange : 'transparent',
          border: `1.5px solid ${open ? theme.orange : theme.border}`,
          borderRadius: '50%',
          cursor: 'pointer',
          // The button itself is small — a "?" the size of a finger would
          // shout louder than the number it explains — so the tap area is
          // stretched around it instead.
          position: 'relative',
        }}
      >
        ?
        <span style={{
          position: 'absolute', top: '50%', left: '50%',
          width: TAP_MIN, height: TAP_MIN,
          transform: 'translate(-50%, -50%)',
        }} />
      </button>

      {open ? (
        <span
          role="note"
          style={{
            position: 'absolute', top: 28, zIndex: 30,
            [side]: 0,
            width: 250,
            // Never wider than the screen it has to fit on.
            maxWidth: 'calc(100vw - 32px)',
            display: 'block',
            background: theme.card,
            border: `2px solid ${theme.orange}`,
            borderRadius: radius.md,
            padding: '12px 14px',
            fontSize: 14, lineHeight: 1.5, fontWeight: 400,
            color: theme.text2, textAlign: 'left',
            boxShadow: '0 8px 24px rgba(21,32,43,0.18)',
          }}
        >
          {children}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false) }}
            style={{
              fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
              color: theme.orangeDark, background: 'transparent',
              border: 'none', cursor: 'pointer',
              padding: '8px 0 0', marginTop: 4,
            }}
          >
            Got it
          </button>
        </span>
      ) : null}
    </span>
  )
}
